import { z } from 'zod';

export const recommendedAgentTaskSchema = z.object({
    automatablePercentage: z.number().nullish(),
    hoursPerWeek: z.number().nullish(),
    taskId: z.string(),
    taskVersion: z.number(),
    title: z.string(),
});

export type RecommendedAgentTask = z.infer<typeof recommendedAgentTaskSchema>;

export const recommendedAgentSchema = z.object({
    agentId: z.string(),
    automationTypes: z.string().array(),
    autonomyLevel: z.number().nullish(),
    businessFunction: z.string().nullish(),
    catalogueStatus: z.string().nullish(),
    effortShare: z.number().nullish(),
    hoursPerWeek: z.number(),
    shortDefinition: z.string().nullish(),
    tasks: recommendedAgentTaskSchema.array(),
    title: z.string().nullish(),
});

export type RecommendedAgent = z.infer<typeof recommendedAgentSchema>;

export const recommendedAgentsResponseSchema = z.object({
    agents: recommendedAgentSchema.array(),
    hoursPerWeek: z.number(),
    recommendedTaskCount: z.number(),
    taskCount: z.number(),
});

export type RecommendedAgentsResponse = z.infer<typeof recommendedAgentsResponseSchema>;

export const agentLibraryEntrySchema = z.object({
    agentId: z.string(),
    autonomyLevel: z.number().nullish(),
    businessFunction: z.string().nullish(),
    catalogueStatus: z.string().nullish(),
    levelCount: z.number().default(0),
    positionCount: z.number().nullish(),
    roleCount: z.number().default(0),
    shortDefinition: z.string().nullish(),
    taskCount: z.number().default(0),
    title: z.string().nullish(),
});

export type AgentLibraryEntry = z.infer<typeof agentLibraryEntrySchema>;

/** A job role, job level or position the library can be filtered by, with the title of what it belongs to. */
export const agentLibraryHolderSchema = z.object({
    id: z.string(),
    parentTitle: z.string().nullish(),
    title: z.string(),
});

export type AgentLibraryHolder = z.infer<typeof agentLibraryHolderSchema>;

export const agentLibraryResponseSchema = z.object({
    agents: agentLibraryEntrySchema.array(),
    businessFunctions: z.string().array().default([]),
    levels: agentLibraryHolderSchema.array().default([]),
    nextCursor: z.string().nullable(),
    positions: agentLibraryHolderSchema.array().default([]),
    roles: agentLibraryHolderSchema.array().default([]),
    total: z.number(),
});

export type AgentLibraryResponse = z.infer<typeof agentLibraryResponseSchema>;

export const agentTriggerSchema = z.object({
    description: z.string().nullish(),
    kind: z.string().nullish(),
});

export const agentStepSchema = z.object({
    actor: z.string().nullish(),
    description: z.string().nullish(),
    phase: z.string().nullish(),
    title: z.string(),
});

export type AgentStep = z.infer<typeof agentStepSchema>;

export const agentInstructionsSchema = z.object({
    brief: z.string().nullish(),
    constraints: z.string().array().nullish(),
    steps: agentStepSchema.array().nullish(),
    trigger: agentTriggerSchema.nullish(),
});

export type AgentInstructions = z.infer<typeof agentInstructionsSchema>;

export const agentAttributionSchema = z.object({
    effortShare: z.number().nullish(),
    hoursPerWeek: z.number().default(0),
    id: z.string(),
    parentTitle: z.string().nullish(),
    taskCount: z.number().default(0),
    title: z.string().nullish(),
});

export type AgentAttribution = z.infer<typeof agentAttributionSchema>;

/** The agent's best-fitting recommendation in scope: the task it fits best and what the recommender reported for it. */
export const agentBestFitSchema = z.object({
    automatablePercentage: z.number().nullish(),
    automationReasoning: z.string().nullish(),
    complexityLevel: z.enum(['low', 'medium', 'high']).nullish().catch(null),
    products: z.object({ name: z.string(), productId: z.string() }).array().default([]),
    score: z.number().min(0).max(1),
    scoreReasoning: z.string().nullish(),
    taskId: z.string(),
    taskTitle: z.string(),
    workflows: z
        .object({
            automatablePercentage: z.number().nullish(),
            autonomyLevel: z.number().nullish(),
            complexityLevel: z.enum(['low', 'medium', 'high']).nullish().catch(null),
            reasoning: z.string().nullish(),
            workflowIndex: z.number().nullish(),
        })
        .array()
        .default([]),
});

export type AgentBestFit = z.infer<typeof agentBestFitSchema>;

export const agentAttributionsResponseSchema = z.object({
    agent: agentLibraryEntrySchema.extend({
        bestFit: agentBestFitSchema.nullish(),
        instructions: agentInstructionsSchema.nullish(),
    }),
    levels: agentAttributionSchema.array().default([]),
    positions: agentAttributionSchema.array().default([]),
    roles: agentAttributionSchema.array().default([]),
});

export type AgentAttributionsResponse = z.infer<typeof agentAttributionsResponseSchema>;
